import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { DropdownToggle } from 'reactstrap';
import styled from 'styled-components';

import { APIConfig } from 'src/config';
import { carbonLabel } from 'src/enosikit/components/Chart/helpers/common';
import LinkInI18nBlock from 'src/enosikit/components/LinkInI18nBlock';
import getStartFormat from 'src/helpers/dateRangeHelpers';
import FlashesStore from 'src/stores/FlashesStore';
import {
  INHIBIT_CARBON_DATA_VIEWS, SOURCE_HISTORIAN,
  UNIT, UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY,
} from 'src/util/constants';
import { getLocale } from 'src/util/i18n/handler';

export const ViewWrapper = styled(DropdownToggle)`
{
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
`;

/**
 * Provides the input for the chart display (carbon/currency/wh) type selector
 * @param {string} unit
 * @param {string} source
 * @param {boolean} isPortfolio - Carbon view is disabled for portfolio (release 1)
 * @returns {Array<object>} - chart display control options.
 */
export const controlOptionFunc = (unit, source, isPortfolio) => {
  const inhibitCarbonDataViews = APIConfig().feature(INHIBIT_CARBON_DATA_VIEWS);
  const unitOps = {
    group: UNIT,
    items: [{
      value: UNIT_ENERGY, label: 'Wh', active: (unit === UNIT_ENERGY), disabled: false,
    }, {
      value: UNIT_CURRENCY, label: <FormattedMessage id="dashboard_control.helpers.common.type_selector.currency.label" defaultMessage="$" />, active: (unit === UNIT_CURRENCY), disabled: (source === SOURCE_HISTORIAN),
    }],
  };
  if (inhibitCarbonDataViews || isPortfolio) {
    return [unitOps];
  }

  const carbonController = {
    value: UNIT_CARBON, label: carbonLabel(), active: (unit === UNIT_CARBON),
  };
  unitOps.items.push(carbonController);
  return [unitOps];
};

/**
 * @constant
 * @type {number}
 * @default
 */
export const WARNING_TIMEOUT = 5000;

/**
 * @param {import('react-intl').IntlShape} intl - i18n
 * @returns {React.ReactComponentElement} - warning dialog fragment
 */
export const warningDialog = (intl) => {
  const msgWarning = (
    <FormattedMessage
      id="helpers.dashboard.trade_rules.message_warning"
      defaultMessage="Hi! Are you trying to view your trade rules? Create and manage them by selecting Trade Rules in the menu above."
      values={{
        link: (chunks) => (
          <LinkInI18nBlock
            url={intl.formatMessage({ id: 'helpers.dashboard.trade_rules.message_warning.url', defaultMessage: 'helpers.dashboard.trade_rules.message_warning.url' })}
            urlKey="helpers.dashboard.trade_rules.message_warning.url"
            chunks={chunks}
          />
        ),
      }}
    />
  );
  return (
    <>
      <FontAwesomeIcon className="me-1" size="lg" icon={faTriangleExclamation} />
      {msgWarning}
    </>
  );
};

/**
 * Creates the warning dialog when the user in dashboard trades view
 * clicks on trades toggle button more than once in 5s
 * @param {import('react-intl').IntlShape} intl - i18n
 * @returns {void}
 */
export const showTradeRuleMsg = (intl) => {
  clearTimeout(undefined);

  const tradeRulesNav = document.querySelectorAll('nav.navbar .nav-item.nav-trade-rules');

  if (tradeRulesNav && tradeRulesNav[0]) {
    FlashesStore.flash(
      FlashesStore.WARNING,
      warningDialog(intl),
    );
    tradeRulesNav[0].classList?.add('trade-rules-highlight');
    tradeRulesNav[0].focus();
    setTimeout(() => {
      tradeRulesNav[0].classList?.remove('trade-rules-highlight');
      FlashesStore.reset();
    }, WARNING_TIMEOUT);
  }
};

/**
 * Label to use to indicate selected daterange in the HTML.
 * @param {DateTime} s start of daterange selected.
 * @param {DateTime} f finish of the daterange selected.
 * @param {string} timezone
 * @returns {string} daterange label.
 */
export const timeSpanButtonLabel = (s, f, timezone = 'system') => {
  if (!s || !f) { return ''; }

  const appLocale = getLocale();

  // If it's for a single day, display a single day
  if (s.hasSame(f, 'day')) {
    return s.setLocale(appLocale).setZone(timezone).toFormat('DD');
  }

  // Otherwise let's make a nice label
  const startFormat = getStartFormat(s, f);

  const startDate = s.setLocale(appLocale).setZone(timezone).toFormat(startFormat);
  const finishDate = f.setLocale(appLocale).setZone(timezone).toFormat('DD');

  return [startDate, finishDate].flat().join(' - ');
};
